package com.tovtalk.data.repository

import com.tovtalk.data.mapper.toPost
import com.tovtalk.data.network.InFlightRequestDeduplicator
import com.tovtalk.data.remote.ApiClient
import com.tovtalk.data.remote.ApiEndpoints
import com.tovtalk.data.remote.dto.*
import com.tovtalk.domain.entity.*
import com.tovtalk.domain.repository.PostRepository
import com.tovtalk.utils.logger.Logger
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*

/**
 * Post Repository Implementation (v2 API — cursor pagination, unified feed)
 */
class PostRepositoryImpl(
    private val apiClient: ApiClient
) : PostRepository {

    private val feedDedup = InFlightRequestDeduplicator<String>()

    // ── Feed ─────────────────────────────────────────────────────────────────

    override suspend fun getDiscoverFeed(cursor: String?, limit: Int): DomainResult<FeedPage> =
        feedDedup.run("discover::${cursor ?: ""}:$limit") {
            try {
                Logger.d("TT_VIDEO: server_request | discover | start", "TT_VIDEO")
                val start = com.tovtalk.utils.nowInstant()

                val response = apiClient.httpClient.get(ApiEndpoints.feed("discover", cursor = cursor, limit = limit))
                    .body<V2Response<V2PaginatedData<PostDtoV2>>>()

                val errors = response.errors
                if (!errors.isNullOrEmpty()) {
                    Logger.d("TT_VIDEO: server_request | discover | fail | msg=${errors.first().message}", "TT_VIDEO")
                    return@run DomainResult.Error(errors.first().message)
                }

                val data = response.data ?: return@run DomainResult.Error("No data")
                val durationMs = (com.tovtalk.utils.nowInstant().toEpochMilliseconds() - start.toEpochMilliseconds())
                Logger.d("TT_VIDEO: server_request | discover | success | count=${data.items.size} | durationMs=$durationMs", "TT_VIDEO")

                val posts = data.items.map { it.toPost(apiClient.bandwidthEstimator) }
                    .filter { it.isPermanent }
                DomainResult.Success(FeedPage(posts, data.nextCursor, data.kashrutRestricted ?: false))
            } catch (e: Exception) {
                Logger.e("TT_VIDEO: server_request | discover | fail | msg=${e.message}", "TT_VIDEO", e)
                DomainResult.Error("Network error: ${e.message}", e)
            }
        }

    override suspend fun getFollowingFeed(cursor: String?, limit: Int): DomainResult<FeedPage> =
        feedDedup.run("following::${cursor ?: ""}:$limit") {
            try {
                val response = apiClient.httpClient.get(ApiEndpoints.feed("following", cursor = cursor, limit = limit))
                    .body<V2Response<V2PaginatedData<PostDtoV2>>>()

                response.errors?.takeIf { it.isNotEmpty() }?.let { errors ->
                    return@run DomainResult.Error(errors.first().message)
                }

                val data = response.data ?: return@run DomainResult.Error("No data")
                val posts = data.items.map { it.toPost(apiClient.bandwidthEstimator) }
                    .filter { it.isPermanent }
                DomainResult.Success(FeedPage(posts, data.nextCursor, data.kashrutRestricted ?: false))
            } catch (e: Exception) {
                DomainResult.Error("Network error: ${e.message}", e)
            }
        }

    override suspend fun getUserFeed(userId: Int, cursor: String?, limit: Int): DomainResult<FeedPage> =
        feedDedup.run("user:$userId:${cursor ?: ""}:$limit") {
            try {
                Logger.d("TT_VIDEO: server_request | user_posts | userId=$userId | start", "TT_VIDEO")

                val response = apiClient.httpClient.get(ApiEndpoints.feed("user", cursor = cursor, limit = limit, userId = userId))
                    .body<V2Response<V2PaginatedData<PostDtoV2>>>()

                response.errors?.takeIf { it.isNotEmpty() }?.let { errors ->
                    Logger.d("TT_VIDEO: server_request | user_posts | userId=$userId | fail | msg=${errors.first().message}", "TT_VIDEO")
                    return@run DomainResult.Error(errors.first().message)
                }

                val data = response.data ?: return@run DomainResult.Error("No data")
                Logger.d("TT_VIDEO: server_request | user_posts | userId=$userId | success | count=${data.items.size}", "TT_VIDEO")

                val posts = data.items.map { it.toPost(apiClient.bandwidthEstimator) }
                    .filter { it.isPermanent }
                DomainResult.Success(FeedPage(posts, data.nextCursor, data.kashrutRestricted ?: false))
            } catch (e: Exception) {
                Logger.e("TT_VIDEO: server_request | user_posts | fail | msg=${e.message}", "TT_VIDEO", e)
                DomainResult.Error("Network error: ${e.message}", e)
            }
        }

    override suspend fun getUserStories(userId: Int): DomainResult<List<Post>> = try {
        val response = apiClient.httpClient.get(ApiEndpoints.userStories(userId))
            .body<V2Response<V2PaginatedData<PostDtoV2>>>()

        response.errors?.takeIf { it.isNotEmpty() }?.let { errors ->
            return DomainResult.Error(errors.first().message)
        }

        val data = response.data ?: return DomainResult.Error("No data")
        val posts = data.items.map { it.toPost(apiClient.bandwidthEstimator) }
        DomainResult.Success(posts)
    } catch (e: Exception) {
        DomainResult.Error("Network error: ${e.message}", e)
    }

    override suspend fun getSavedFeed(cursor: String?, limit: Int): DomainResult<FeedPage> =
        feedDedup.run("saved::${cursor ?: ""}:$limit") {
            try {
                val response = apiClient.httpClient.get(ApiEndpoints.feed("saved", cursor = cursor, limit = limit))
                    .body<V2Response<V2PaginatedData<PostDtoV2>>>()

                response.errors?.takeIf { it.isNotEmpty() }?.let { errors ->
                    return@run DomainResult.Error(errors.first().message)
                }

                val data = response.data ?: return@run DomainResult.Error("No data")
                val posts = data.items.map { it.toPost(apiClient.bandwidthEstimator) }
                DomainResult.Success(FeedPage(posts, data.nextCursor, data.kashrutRestricted ?: false))
            } catch (e: Exception) {
                DomainResult.Error("Network error: ${e.message}", e)
            }
        }

    // ── Single post ───────────────────────────────────────────────────────────

    override suspend fun getPost(ref: String): DomainResult<Post> {
        return try {
            Logger.d("TT_VIDEO: server_request | get_post | ref=$ref | start", "TT_VIDEO")
            val start = com.tovtalk.utils.nowInstant()

            val response = apiClient.httpClient.get(ApiEndpoints.post(ref))
                .body<V2Response<SinglePostWrapper>>()

            val durationMs = (com.tovtalk.utils.nowInstant().toEpochMilliseconds() - start.toEpochMilliseconds())

            response.errors?.takeIf { it.isNotEmpty() }?.let { errors ->
                Logger.d("TT_VIDEO: server_request | get_post | ref=$ref | fail | msg=${errors.first().message} | durationMs=$durationMs", "TT_VIDEO")
                return DomainResult.Error(errors.first().message)
            }

            val wrapper = response.data
            if (wrapper == null) {
                return DomainResult.Error("Post not found")
            }
            if (wrapper.kashrutRestricted) {
                return DomainResult.Error("Content restricted by kashrut settings", errorCode = ERROR_CODE_KASHRUT_NOT_ALLOWED)
            }
            val dto = wrapper.item ?: return DomainResult.Error("Post not found")
            Logger.d("TT_VIDEO: server_request | get_post | ref=$ref | success | durationMs=$durationMs", "TT_VIDEO")
            DomainResult.Success(dto.toPost(apiClient.bandwidthEstimator))
        } catch (e: Exception) {
            Logger.e("TT_VIDEO: server_request | get_post | ref=$ref | fail | msg=${e.message}", "TT_VIDEO", e)
            DomainResult.Error("Network error: ${e.message}", e)
        }
    }

    // ── CRUD ──────────────────────────────────────────────────────────────────

    override suspend fun createPost(
        caption: String?,
        videoPath: String,
        hashtags: List<String>,
        musicId: Int?,
        isPermanent: Boolean,
        contentSource: String?
    ): DomainResult<Post> = try {
        val request = PostCreateRequest(
            postType = "reel",
            caption = caption,
            hashtags = hashtags,
            soundId = musicId,
            videoPath = videoPath,
            isPermanent = isPermanent,
            contentSource = contentSource
        )

        val response = apiClient.httpClient.post(ApiEndpoints.CREATE_POST) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body<V2Response<PostDtoV2>>()

        response.errors?.takeIf { it.isNotEmpty() }?.let { errors ->
            return DomainResult.Error(errors.first().message)
        }

        val dto = response.data ?: return DomainResult.Error("Failed to create post")
        DomainResult.Success(dto.toPost(apiClient.bandwidthEstimator))
    } catch (e: Exception) {
        DomainResult.Error("Error: ${e.message}", e)
    }

    override suspend fun deletePost(postId: Int): DomainResult<Unit> = try {
        val response = apiClient.httpClient.delete("${ApiEndpoints.DELETE_POST}$postId")
            .body<V2Response<DeleteActionData>>()

        response.errors?.takeIf { it.isNotEmpty() }?.let { errors ->
            return DomainResult.Error(errors.first().message)
        }
        DomainResult.Success(Unit)
    } catch (e: Exception) {
        DomainResult.Error("Network error: ${e.message}", e)
    }

    // ── Actions ───────────────────────────────────────────────────────────────

    override suspend fun likePost(postId: Int): DomainResult<Unit> = try {
        val response = apiClient.httpClient.post(ApiEndpoints.postLike(postId))
            .body<V2Response<LikeActionData>>()
        response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
        DomainResult.Success(Unit)
    } catch (e: Exception) {
        DomainResult.Error("Network error: ${e.message}", e)
    }

    override suspend fun unlikePost(postId: Int): DomainResult<Unit> = try {
        val response = apiClient.httpClient.post(ApiEndpoints.postUnlike(postId))
            .body<V2Response<UnlikeActionData>>()
        response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
        DomainResult.Success(Unit)
    } catch (e: Exception) {
        DomainResult.Error("Network error: ${e.message}", e)
    }

    override suspend fun savePost(postId: Int): DomainResult<Unit> = try {
        val response = apiClient.httpClient.post(ApiEndpoints.postSave(postId))
            .body<V2Response<SaveActionData>>()
        response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
        DomainResult.Success(Unit)
    } catch (e: Exception) {
        DomainResult.Error("Network error: ${e.message}", e)
    }

    override suspend fun unsavePost(postId: Int): DomainResult<Unit> = try {
        val response = apiClient.httpClient.delete(ApiEndpoints.postSave(postId))
            .body<V2Response<UnsaveActionData>>()
        response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
        DomainResult.Success(Unit)
    } catch (e: Exception) {
        DomainResult.Error("Network error: ${e.message}", e)
    }

    override suspend fun viewPost(postId: Int): DomainResult<Unit> = try {
        Logger.d("TT_VIDEO: server_request | view | postId=$postId | start", "TT_VIDEO")
        val response = apiClient.httpClient.post(ApiEndpoints.postView(postId))
            .body<V2Response<ViewActionData>>()
        response.errors?.takeIf { it.isNotEmpty() }?.let { errors ->
            Logger.d("TT_VIDEO: server_request | view | postId=$postId | fail | msg=${errors.first().message}", "TT_VIDEO")
            return DomainResult.Error(errors.first().message)
        }
        Logger.d("TT_VIDEO: server_request | view | postId=$postId | success", "TT_VIDEO")
        DomainResult.Success(Unit)
    } catch (e: Exception) {
        Logger.e("TT_VIDEO: server_request | view | postId=$postId | fail | msg=${e.message}", "TT_VIDEO", e)
        DomainResult.Error("Network error: ${e.message}", e)
    }

    // ── Events batch ──────────────────────────────────────────────────────────

    override suspend fun batchEvents(events: List<EventItem>): DomainResult<EventsBatchResponse> = try {
        val response = apiClient.httpClient.post(ApiEndpoints.EVENTS_BATCH) {
            contentType(ContentType.Application.Json)
            setBody(EventsBatchRequest(events))
        }.body<V2Response<EventsBatchResponse>>()

        response.errors?.takeIf { it.isNotEmpty() }?.let { errors ->
            return DomainResult.Error(errors.first().message)
        }

        val data = response.data ?: return DomainResult.Error("No batch response data")
        DomainResult.Success(data)
    } catch (e: Exception) {
        DomainResult.Error("Network error: ${e.message}", e)
    }

    // ── Report ────────────────────────────────────────────────────────────────

    override suspend fun getReportReasons(appliesTo: String, lang: String): DomainResult<List<ReportReasonOption>> = try {
        val response = apiClient.httpClient.get(ApiEndpoints.REPORT_REASONS) {
            parameter("applies_to", appliesTo)
            parameter("lang", lang)
        }.body<ReportReasonsResponse>()

        val items = response.data?.items
        if (response.errors == null && items != null) {
            val list = items.map { dto ->
                ReportReasonOption(
                    id = dto.id,
                    title = dto.title ?: dto.titleHe ?: dto.titleEn ?: ""
                )
            }
            DomainResult.Success(list)
        } else {
            DomainResult.Error(response.errors ?: "Failed to load report reasons")
        }
    } catch (e: Exception) {
        DomainResult.Error("Network error: ${e.message}", e)
    }

    override suspend fun reportPost(postId: Int, reasonId: Int, description: String): DomainResult<Unit> = try {
        val body = ReportPostRequest(
            postId = postId,
            reasonId = reasonId,
            details = description.takeIf { it.isNotBlank() }
        )
        val response = apiClient.httpClient.post(ApiEndpoints.REPORT_POST) {
            contentType(ContentType.Application.Json)
            setBody(body)
        }.body<V2Response<kotlinx.serialization.json.JsonElement>>()

        val errors = response.errors
        if (errors.isNullOrEmpty()) {
            DomainResult.Success(Unit)
        } else {
            DomainResult.Error(errors.first().message)
        }
    } catch (e: Exception) {
        DomainResult.Error("Network error: ${e.message}", e)
    }
}
